// ============================================
// Config Module (ES6)
// ============================================
// 참고: API Key, 모델 버전 등 민감 정보는 Cloud Function에서 관리됩니다.
// Firebase 설정은 shared/firebase-config.js에서 로드됩니다.

// ============================================
// Firebase & API 설정
// ============================================

// firebaseConfig는 shared/firebase-config.js에서 window.firebaseConfig로 정의됨
export const firebaseConfig = window.firebaseConfig || window.FIREBASE_CONFIG;

// Cloud Functions Base URL
export const FUNCTIONS_BASE = window.FUNCTIONS_BASE_URL || 'https://us-central1-saju-f8122.cloudfunctions.net';

// Cloud Run Base URL (Firebase Hosting 프록시 - 일반 API용)
const isCapacitor = window.Capacitor?.isNativePlatform?.();
export const CLOUD_RUN_BASE = isCapacitor
  ? 'https://sajuai-api-391890616913.asia-northeast3.run.app'
  : '/api';

// Cloud Run 직접 URL (SSE 스트리밍용 - Firebase Hosting 버퍼링 우회)
export const CLOUD_RUN_DIRECT = 'https://sajuai-api-391890616913.asia-northeast3.run.app';

// ============================================
// 이미지 모델 설정
// ============================================

export const IMAGE_MODEL_OPTIONS = [
  { id: 'animagine-4', name: 'Animagine 4.0', description: '고품질 애니메이션 스타일' },
  { id: 'sdxl', name: 'SDXL', description: '범용 고품질 이미지' },
  { id: 'flux-schnell', name: 'Flux Schnell', description: '빠른 생성 속도' }
];

export const DEFAULT_IMAGE_MODEL = 'animagine-4';

// ============================================
// 크레딧 & 채팅 설정
// ============================================

export const CARBON_COST = { CREATE_CHAT: 10, SEND_MESSAGE: 2 };

// ============================================
// AI 모델 설정
// ============================================

export const DEFAULT_MODEL_OPTIONS = [
  {
    id: 'gemini-2.5-flash',
    name: '호신고',
    carbon: 1,
    description: '빠르고 효율적인 응답'
  },
  {
    id: 'deepseek-reasoner',
    name: '유니버스',
    carbon: 2,
    description: '깊은 사고력과 효율적 추론'
  },
  {
    id: 'gemini-3-flash-preview',
    name: '호라이즌',
    carbon: 2,
    description: '깊은 사고력과 창의성'
  },
  {
    id: 'gemini-3-pro-preview',
    name: '뫼비우스',
    carbon: 5,
    description: '최상의 성능과 정교함'
  }
];

// 동적 모델 옵션 (API에서 로드됨)
let _modelOptions = [...DEFAULT_MODEL_OPTIONS];

export function getModelOptions() {
  return _modelOptions;
}

export function setModelOptions(options) {
  _modelOptions = options;
}

// Firestore에서 모델 설정 로드
export async function loadModelOptionsFromServer() {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/loadModelConfigs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error('Failed to load model configs');
    }

    const data = await response.json();

    if (data.success && data.models && data.models.length > 0) {
      _modelOptions = data.models
        .filter(m => m.isActive !== false)
        .map(m => ({
          id: m.id,
          name: m.name,
          carbon: typeof m.carbonCost === 'number' ? m.carbonCost : 2,
          description: m.description || ''
        }));

      console.log('Model options loaded from server:', _modelOptions.length);
      return true;
    }
  } catch (error) {
    console.warn('Failed to load model options from server, using defaults:', error.message);
  }
  return false;
}

// ============================================
// TTS 음성 프리셋 설정
// ============================================

// Chirp 3: HD 30개 음성 프리셋
export const VOICE_PRESETS = {
  // 여성 음성 (14개)
  achernar: {
    id: 'achernar',
    name: '아케르나르',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Achernar'
  },
  aoede: {
    id: 'aoede',
    name: '아오에데',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Aoede'
  },
  autonoe: {
    id: 'autonoe',
    name: '아우토노에',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Autonoe'
  },
  callirrhoe: {
    id: 'callirrhoe',
    name: '칼리로에',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Callirrhoe'
  },
  despina: {
    id: 'despina',
    name: '데스피나',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Despina'
  },
  erinome: {
    id: 'erinome',
    name: '에리노메',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Erinome'
  },
  gacrux: {
    id: 'gacrux',
    name: '가크룩스',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Gacrux'
  },
  kore: {
    id: 'kore',
    name: '코레',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Kore'
  },
  laomedeia: {
    id: 'laomedeia',
    name: '라오메데이아',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Laomedeia'
  },
  leda: {
    id: 'leda',
    name: '레다',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Leda'
  },
  pulcherrima: {
    id: 'pulcherrima',
    name: '풀케리마',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Pulcherrima'
  },
  sulafat: {
    id: 'sulafat',
    name: '술라파트',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Sulafat'
  },
  vindemiatrix: {
    id: 'vindemiatrix',
    name: '빈데미아트릭스',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Vindemiatrix'
  },
  zephyr: {
    id: 'zephyr',
    name: '제퍼',
    gender: '여성',
    voiceName: 'ko-KR-Chirp3-HD-Zephyr'
  },

  // 남성 음성 (16개)
  achird: {
    id: 'achird',
    name: '아키르드',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Achird'
  },
  algenib: {
    id: 'algenib',
    name: '알게니브',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Algenib'
  },
  algieba: {
    id: 'algieba',
    name: '알기에바',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Algieba'
  },
  alnilam: {
    id: 'alnilam',
    name: '알닐람',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Alnilam'
  },
  charon: {
    id: 'charon',
    name: '카론',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Charon'
  },
  enceladus: {
    id: 'enceladus',
    name: '엔셀라두스',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Enceladus'
  },
  fenrir: {
    id: 'fenrir',
    name: '펜리르',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Fenrir'
  },
  iapetus: {
    id: 'iapetus',
    name: '이아페투스',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Iapetus'
  },
  orus: {
    id: 'orus',
    name: '오루스',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Orus'
  },
  puck: {
    id: 'puck',
    name: '퍽',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Puck'
  },
  rasalgethi: {
    id: 'rasalgethi',
    name: '라살게티',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Rasalgethi'
  },
  sadachbia: {
    id: 'sadachbia',
    name: '사다크비아',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Sadachbia'
  },
  sadaltager: {
    id: 'sadaltager',
    name: '사달타게르',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Sadaltager'
  },
  schedar: {
    id: 'schedar',
    name: '셰다르',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Schedar'
  },
  umbriel: {
    id: 'umbriel',
    name: '움브리엘',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Umbriel'
  },
  zubenelgenubi: {
    id: 'zubenelgenubi',
    name: '주벤엘게누비',
    gender: '남성',
    voiceName: 'ko-KR-Chirp3-HD-Zubenelgenubi'
  }
};

// 성별별 음성 목록 헬퍼
export function getVoicesByGender(gender) {
  return Object.values(VOICE_PRESETS).filter(v => v.gender === gender);
}

// 음성 프리셋 조회
export function getVoicePreset(voiceId) {
  return VOICE_PRESETS[voiceId] || null;
}

// ============================================
// 캐시 설정
// ============================================

export const IMAGE_CACHE_KEY = 'persona_image_cache';
export const IMAGE_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24시간

// ============================================
// 통계 배치 처리는 analytics/stats-buffer.js에서 직접 import
// import { recordImpression, recordClick } from './analytics/stats-buffer.js'
// ============================================
